// @flow

import * as React from 'react';

import clsx from 'clsx';
import { Link } from 'react-router-dom';

import { selectCartUUID } from '@gandi/cart';
import type {
  ContactRole,
  FavoriteContact,
} from '@gandi/redux-resource-contactbook';
import { useTranslator, mapTags } from '@gandi/translate';
import { Base } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { getModifyContactUrl } from '../../../modules/helpers';
import { selectHasDomainTransfer } from '../../../modules/selectors';
import type { CreateOrTransfer } from '../../../types';

import SwitchIndicator from './RoleContact/SwitchIndicator';

import styles from './EmptyRoleContact.scss';

type OwnProps = {
  role: ContactRole,
  process: CreateOrTransfer,
  owner: FavoriteContact | void,
  readOnly: boolean,
};

type Props = { ...OwnProps };

function EmptyRoleContact({
  role,
  owner,
  process,
  readOnly,
}: Props): React.MixedElement {
  const { __ } = useTranslator();
  const cartId = useAppSelector(selectCartUUID);

  const baseArgs = {
    paddingTop: 'xsmall',
    paddingLeft: 'small',
    paddingRight: 'small',
    backgroundColor: 'grey-lighter',
  };
  const hasDomainTransfer = useAppSelector(selectHasDomainTransfer);
  return (
    <>
      <Base {...baseArgs} paddingBottom="xsmall">
        <div className={clsx(styles.placeholder, styles.width8)} />
        <div className={clsx(styles.placeholder, styles.width11)} />
        <div
          className={clsx(styles.placeholder, styles.smallSize, styles.width4)}
        />
      </Base>
      <Base {...baseArgs} paddingBottom="xsmall">
        <div className={clsx(styles.placeholder, styles.width15)} />
        <div className={clsx(styles.placeholder, styles.width5)} />
        <div className={clsx(styles.placeholder, styles.width11)} />
        <div className={clsx(styles.placeholder, styles.width6)} />
      </Base>
      <Base {...baseArgs}>
        <SwitchIndicator enabled={owner?.mail_obfuscated} />
        <div
          className={clsx(styles.placeholder, styles.width6, styles.inline)}
        />
      </Base>
      <Base {...baseArgs}>
        <SwitchIndicator enabled={owner?.data_obfuscated} />
        <div
          className={clsx(styles.placeholder, styles.width7, styles.inline)}
        />
      </Base>

      {hasDomainTransfer && (
        <Base {...baseArgs} paddingBottom="small">
          <SwitchIndicator enabled={owner?.send_invitation} />
          <div
            className={clsx(styles.placeholder, styles.width7, styles.inline)}
          />
        </Base>
      )}

      <Base
        {...baseArgs}
        paddingTop="small"
        paddingBottom="small"
        color="light"
      >
        {readOnly
          ? __('Same as owner information.')
          : mapTags(
              __('Same as owner information. [[changeLink:Change]]'),
              ({ changeLink }) => ({
                changeLink: (
                  <Link to={getModifyContactUrl(cartId, process, role)}>
                    {changeLink}
                  </Link>
                ),
              }),
            )}
      </Base>
    </>
  );
}

export default (React.memo<Props>(
  EmptyRoleContact,
): React.ComponentType<Props>);
